/**
 * IUROP Product List Carousel
 * Handles carousel navigation with arrow buttons
 * Supports Shopify Product Recommendations API
 */

(function () {
  // Guard: la home usa varias secciones `iurop-product-list`, cada una emite este
  // <script>. Sin el envoltorio, la 2ª carga redeclara `class IuropProductListCarousel`
  // y lanza "Identifier already declared". El IIFE aísla el scope por ejecución.
  if (window.__iuropProductListLoaded) return;
  window.__iuropProductListLoaded = true;

class IuropProductListCarousel {
  constructor(section) {
    this.section = section;
    this.carousel = section.querySelector("[data-carousel-track]");
    this.prevButton = section.querySelector("[data-carousel-prev]");
    this.nextButton = section.querySelector("[data-carousel-next]");

    if (!this.carousel) return;

    this.cards = this.carousel.querySelectorAll(".iurop-product-list__card");
    this.cardWidth = 0;
    this.gap = 0;
    this.scrollPosition = 0;
    this.maxScroll = 0;

    // Check if this section uses recommendations
    this.isRecommendations = section.hasAttribute(
      "data-product-recommendations",
    );

    if (this.isRecommendations) {
      this.loadRecommendations();
    } else {
      this.init();
    }
  }

  async loadRecommendations() {
    const productId = this.section.dataset.productId;
    const sectionId = this.section.dataset.sectionId;
    const limit = this.section.dataset.limit || 10;
    const baseUrl = this.section.dataset.recommendationsUrl || '/recommendations/products';

    if (!productId) {
      console.warn('IUROP Product List: Missing product ID for recommendations');
      this.init();
      return;
    }

    try {
      // Shopify recommendations API URL
      const url = `${baseUrl}?product_id=${productId}&limit=${limit}&section_id=${sectionId}&intent=related`;
      
      const response = await fetch(url);
      if (!response.ok) throw new Error("Failed to fetch recommendations: " + response.status);

      const html = await response.text();
      const tempDiv = document.createElement("div");
      tempDiv.innerHTML = html;

      // The response is the section HTML. We need the content inside [data-carousel-track]
      let newTrack = tempDiv.querySelector("[data-carousel-track]");
      
      // If not found, maybe the response is just the inner HTML of the track? (unlikely but safe)
      if (!newTrack && html.includes('iurop-product-list__card')) {
         newTrack = tempDiv;
      }

      if (newTrack) {
        const productCards = newTrack.querySelectorAll(".iurop-product-list__card:not(.iurop-product-list__card--placeholder)");
        
        if (productCards.length > 0) {
          this.carousel.innerHTML = '';
          productCards.forEach(card => {
            this.carousel.appendChild(card.cloneNode(true));
          });
          
          this.cards = this.carousel.querySelectorAll(".iurop-product-list__card");
          this.section.style.display = ''; // Ensure it's visible
        } else {
          // No products even with liquid fallback
          console.warn('IUROP Product List: No products found in recommendations response');
          this.section.style.display = 'none';
        }
      } else {
        console.warn('IUROP Product List: Could not find carousel track in response');
      }
    } catch (error) {
      console.error("IUROP Product List: Error loading recommendations", error);
    }

    this.init();
    if (typeof window.initColorSwatches === "function") {
      window.initColorSwatches();
    }
  }

  init() {
    this.calculateDimensions();
    this.bindEvents();
    this.updateArrowStates();
    this.bindMobileCartButtons();
    this.initMobilePagination();
  }

  isMobile() {
    return window.innerWidth <= 749;
  }

  initMobilePagination() {
    this.mobilePageIndex = 0;
    this.mobilePageSize = 4;
    if (this.isMobile()) {
      this.applyMobilePagination();
    }
  }

  applyMobilePagination() {
    const cards = Array.from(this.cards);
    const start = this.mobilePageIndex * this.mobilePageSize;
    const end = start + this.mobilePageSize;

    cards.forEach((card, i) => {
      if (i >= start && i < end) {
        card.classList.remove('is-hidden-mobile');
      } else {
        card.classList.add('is-hidden-mobile');
      }
    });

    this.updateArrowStates();
  }

  getMobileTotalPages() {
    return Math.ceil(this.cards.length / this.mobilePageSize);
  }

  bindMobileCartButtons() {
    this.section.querySelectorAll('.iurop-product-list__cart-icon-btn').forEach(function(btn) {
      btn.addEventListener('click', function(e) {
        e.preventDefault();
        e.stopPropagation();

        if (btn.dataset.openVariantPopup === 'true' && btn.dataset.productHandle && window.iuropVariantPopup) {
          window.iuropVariantPopup.open(btn.dataset.productHandle);
          return;
        }

        var variantId = btn.dataset.variantId;
        if (!variantId || btn.disabled) return;

        if (typeof window.iuropAddToCart === 'function') {
          window.iuropAddToCart({
            variantId: String(variantId),
            quantity: 1,
            button: btn,
            sourceId: 'iurop-product-list',
            openSidecartAfter: false,
            busyText: '...',
            successText: '✓'
          }).catch(function(err) {
            console.error('[IUROP] Error adding to cart:', err);
            btn.disabled = false;
          });
          return;
        }

        // Fallback path if sidecart helper is not available yet.
        btn.disabled = true;
        fetch('/cart/add.js', {
          method: 'POST',
          headers: { 'Content-Type': 'application/json' },
          body: JSON.stringify({ id: String(variantId), quantity: 1 })
        })
          .then(function(res) { return res.json(); })
          .then(function() { return fetch('/cart.js').then(function(r) { return r.json(); }); })
          .then(function(cart) {
            btn.disabled = false;
            document.dispatchEvent(new CustomEvent('cart:update', {
              bubbles: true,
              detail: {
                resource: cart,
                sourceId: 'iurop-product-list',
                data: { source: 'iurop-product-list', itemCount: cart.item_count || 0 }
              }
            }));
          })
          .catch(function(err) {
            console.error('[IUROP] Error adding to cart:', err);
            btn.disabled = false;
          });
      });
    });
  }

  calculateDimensions() {
    if (this.cards.length === 0) return;

    const card = this.cards[0];
    const computedStyle = window.getComputedStyle(this.carousel);

    this.cardWidth = card.offsetWidth;
    this.gap = parseInt(computedStyle.gap) || 24;
    this.visibleWidth = this.carousel.parentElement.offsetWidth;
    this.totalWidth =
      (this.cardWidth + this.gap) * this.cards.length - this.gap;
    this.maxScroll = Math.max(0, this.totalWidth - this.visibleWidth);
  }

  bindEvents() {
    if (this.prevButton) {
      this.prevButton.addEventListener("click", () => this.scrollPrev());
    }

    if (this.nextButton) {
      this.nextButton.addEventListener("click", () => this.scrollNext());
    }

    // Update on scroll (for touch scrolling)
    this.carousel.addEventListener("scroll", () => {
      this.scrollPosition = this.carousel.scrollLeft;
      this.updateArrowStates();
    });

    // Recalculate on resize
    window.addEventListener("resize", () => {
      this.calculateDimensions();
      if (this.isMobile()) {
        this.applyMobilePagination();
      } else {
        // Remove mobile hidden classes on desktop
        Array.from(this.cards).forEach(card => card.classList.remove('is-hidden-mobile'));
        this.updateArrowStates();
      }
    });
  }

  scrollPrev() {
    if (this.isMobile()) {
      if (this.mobilePageIndex > 0) {
        this.mobilePageIndex--;
        this.applyMobilePagination();
      }
      return;
    }

    const scrollAmount = this.cardWidth + this.gap;
    const newPosition = Math.max(0, this.carousel.scrollLeft - scrollAmount);

    this.carousel.scrollTo({
      left: newPosition,
      behavior: "smooth",
    });

    this.scrollPosition = newPosition;
    setTimeout(() => this.updateArrowStates(), 400);
  }

  scrollNext() {
    if (this.isMobile()) {
      if (this.mobilePageIndex < this.getMobileTotalPages() - 1) {
        this.mobilePageIndex++;
        this.applyMobilePagination();
      }
      return;
    }

    const scrollAmount = this.cardWidth + this.gap;
    const newPosition = Math.min(
      this.maxScroll,
      this.carousel.scrollLeft + scrollAmount,
    );

    this.carousel.scrollTo({
      left: newPosition,
      behavior: "smooth",
    });

    this.scrollPosition = newPosition;
    setTimeout(() => this.updateArrowStates(), 400);
  }

  updateArrowStates() {
    let isAtStart, isAtEnd;

    if (this.isMobile()) {
      isAtStart = this.mobilePageIndex === 0;
      isAtEnd = this.mobilePageIndex >= this.getMobileTotalPages() - 1;
    } else {
      const currentScroll = this.carousel.scrollLeft;
      isAtStart = currentScroll <= 0;
      isAtEnd = currentScroll >= this.maxScroll - 1;
    }

    if (this.prevButton) {
      this.prevButton.classList.toggle("is-active", !isAtStart);
      this.prevButton.disabled = isAtStart;
    }

    if (this.nextButton) {
      this.nextButton.classList.toggle("is-inactive", isAtEnd);
      this.nextButton.disabled = isAtEnd;
    }
  }
}

// Initialize all product list carousels on the page
document.addEventListener("DOMContentLoaded", () => {
  const sections = document.querySelectorAll(".iurop-product-list");
  sections.forEach((section) => new IuropProductListCarousel(section));
});

// Re-initialize on Shopify section load (for theme editor)
if (typeof Shopify !== "undefined" && Shopify.designMode) {
  document.addEventListener("shopify:section:load", (event) => {
    const section = event.target.querySelector(".iurop-product-list");
    if (section) {
      new IuropProductListCarousel(section);
    }
  });
}
})();
